import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  listInvoices,
  markInvoicePaid,
  type Invoice,
  type InvoiceStatus,
} from "../api/invoices";
import { formatCurrencyAmount } from "./BillingStatsView";

const STATUS_META: Record<
  InvoiceStatus,
  { label: string; color: "default" | "success" | "warning" | "error" }
> = {
  pending: { label: "Pendiente", color: "warning" },
  paid: { label: "Pagada", color: "success" },
  overdue: { label: "Vencida", color: "error" },
  void: { label: "Anulada", color: "default" },
};

interface Props {
  merchantId?: string;
  showMerchantColumn?: boolean;
}

export function InvoicesSection({ merchantId, showMerchantColumn = false }: Props) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    listInvoices({ merchantId, limit: 50 })
      .then((res) => setInvoices(res.data))
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Error cargando facturas"),
      )
      .finally(() => setLoading(false));
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onMarkPaid = async (id: string) => {
    setBusyId(id);
    try {
      const updated = await markInvoicePaid(id);
      setInvoices((prev) => prev.map((i) => (i.id === id ? updated : i)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error marcando como pagada");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (invoices.length === 0) {
    return (
      <Alert severity="info">
        No hay facturas {merchantId ? "para este comercio" : ""} todavía. Generálas
        desde el botón "Generar facturas".
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      {invoices.map((inv) => (
        <Paper key={inv.id} variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700}>
                  Período {inv.period}
                </Typography>
                <Chip
                  size="small"
                  label={STATUS_META[inv.status].label}
                  color={STATUS_META[inv.status].color}
                />
              </Stack>
              {showMerchantColumn && inv.merchantName && (
                <Typography variant="caption" color="text.secondary">
                  {inv.merchantName}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                {inv.txCount} tx aprobadas · vence{" "}
                {new Date(inv.dueAt).toLocaleDateString()}
              </Typography>
              {inv.selfBillingIntentId && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label="Self-billing"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontFamily="monospace"
                  >
                    {inv.selfBillingIntentId}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Box sx={{ textAlign: { sm: "right" } }}>
              <Typography variant="h6" fontFamily="monospace" fontWeight={700}>
                {formatCurrencyAmount(inv.totalAmount, inv.currency)} {inv.currency}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                mensual {formatCurrencyAmount(inv.monthlyFeeAmount, inv.currency)} ·
                tx {formatCurrencyAmount(inv.txFeesAmount, inv.currency)}
              </Typography>
            </Box>

            {inv.status === "pending" && (
              <Button
                variant="outlined"
                size="small"
                disabled={busyId === inv.id}
                onClick={() => onMarkPaid(inv.id)}
              >
                {busyId === inv.id ? "…" : "Marcar pagada"}
              </Button>
            )}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
